import { NavLink, Outlet } from "react-router-dom";
import { useState } from "react";
import clsx from "clsx";
import { LayoutDashboard, FileStack, Scale, Receipt, FolderOpen, LogOut, Menu, X, Building2 } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useLogout } from "@/api/auth";
import { useMyProfile } from "@/api/portal";
import NotificationBell from "@/components/common/NotificationBell";

interface PortalNavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const PORTAL_NAV_ITEMS: PortalNavItem[] = [
  { to: "/portal", label: "Resumen", icon: LayoutDashboard },
  { to: "/portal/documents", label: "Documentos", icon: FolderOpen },
  { to: "/portal/procedures", label: "Trámites", icon: FileStack },
  { to: "/portal/legal-cases", label: "Jurídico", icon: Scale },
  { to: "/portal/invoices", label: "Facturación", icon: Receipt },
];

function clientDisplayName(profile?: { businessName: string | null; fullName: string | null; tradeName: string | null }): string {
  if (!profile) return "";
  return profile.businessName || profile.tradeName || profile.fullName || "Cliente";
}

/**
 * Layout simplificado para usuarios del Portal del cliente (Fase 19): sin
 * acceso a módulos internos de la firma, solo a su propia información
 * (documentos, trámites, jurídico, facturación) vía /api/portal/*.
 */
export default function PortalLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data: profile } = useMyProfile();

  return (
    <div className="min-h-screen bg-gray-50 lg:flex">
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col transform bg-slate-900 transition-transform lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center gap-2 px-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-500">
            <Building2 className="h-4.5 w-4.5 text-white" size={18} />
          </div>
          <span className="text-base font-semibold tracking-tight text-white">Portal del cliente</span>
          <button
            type="button"
            className="ml-auto text-slate-400 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Cerrar menú"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="mt-2 flex-1 space-y-0.5 px-3">
          {PORTAL_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/portal"}
                className={({ isActive }) =>
                  clsx(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    isActive ? "bg-brand-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )
                }
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={18} strokeWidth={2} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">{clientDisplayName(profile)}</p>
              <p className="truncate text-xs text-slate-400">{user?.email}</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
              onClick={() => logout.mutate()}
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-4 lg:px-6">
          <button
            type="button"
            className="text-gray-500 lg:hidden"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            <Menu size={22} />
          </button>
          <h1 className="text-base font-semibold text-gray-900">Portal del cliente</h1>
          <div className="flex-1" />
          <NotificationBell portal />
        </header>

        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
